import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "../../api/axios";
import {
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  Box,
  TableContainer,
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

function PayslipDetails() {
  const location = useLocation();

  const [pfno, setPfno] = useState("");
  const [runId, setRunId] = useState(null);

  const [payslip, setPayslip] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // ✅ Read run_id + pfno from query string
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const rid = qs.get("run_id");
    const qpPfno = qs.get("pfno");
    if (rid) setRunId(rid);
    if (qpPfno) setPfno(qpPfno);
  }, [location.search]);

  const fetchPayslip = async () => {
    const cleanPfno = (pfno || "").trim();
    if (!cleanPfno) {
      setError("Please enter PF Number.");
      setPayslip(null);
      return;
    }

    setLoading(true);
    setError("");

    try {
      let response;

      if (runId) {
        // ✅ run-specific snapshot (audit-proof)
        response = await axios.get(`/payroll-run/${runId}/payslip`, {
          params: { pfno: cleanPfno },
        });
        setPayslip(response?.data ?? null);
      } else {
        // ✅ fallback to legacy endpoint
        response = await axios.get("/staff/payslipdetails/pfno", {
          params: { pfno: cleanPfno },
        });
        setPayslip(response?.data ?? null);
      }
    } catch (err) {
      setError("Unable to fetch payslip details. Please check PF Number / Run.");
      setPayslip(null);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Auto-fetch if query string provides pfno (and optionally run_id)
  useEffect(() => {
    if ((pfno || "").trim()) fetchPayslip();
  }, [runId]);

  // helper: safe access
  const staff = payslip?.staff || {};
  const salary = payslip?.salary || {};
  const earningsArr = Array.isArray(salary?.earnings) ? salary.earnings : [];
  const deductionsArr = Array.isArray(salary?.deductions) ? salary.deductions : [];

  // ✅ safe money formatting (handles null/undefined/"")
  const money = (n) => {
    const v = Number(n);
    if (!Number.isFinite(v)) return "-";
    return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Grid container justifyContent="center" py={5}>
        <Grid item xs={12} md={11} lg={10}>
          <Paper
            elevation={10}
            sx={{
              p: 5,
              borderRadius: 3,
              background: "#fafafa",
              border: "1px solid #e0e0e0",
              boxShadow: "0px 4px 20px rgba(0,0,0,0.05)",
            }}
          >
            <Box textAlign="center" mb={4}>
              <Typography
                variant="h5"
                fontWeight="bold"
                sx={{ color: "#1976d2", letterSpacing: 1 }}
              >
                EMPLOYEE PAYSLIP
              </Typography>

              {/* ✅ show whether snapshot is being used */}
              <Typography variant="subtitle2" color="text.secondary">
                {runId ? `Payroll Run Snapshot (Run #${runId})` : "Legacy Payslip"}
              </Typography>

              <Typography variant="subtitle1" color="text.secondary">
                {new Date().toLocaleString("default", { month: "long", year: "numeric" })}
              </Typography>

              <Divider sx={{ mt: 2, mb: 3 }} />
            </Box>

            <Grid container spacing={2} mb={4} justifyContent="center" alignItems="center">
              <Grid item xs={12} sm={8} md={6}>
                <TextField
                  fullWidth
                  label="Enter PF Number"
                  variant="outlined"
                  value={pfno}
                  onChange={(e) => setPfno(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") fetchPayslip();
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={fetchPayslip}
                  disabled={loading}
                  sx={{
                    height: "56px",
                    fontWeight: "bold",
                    textTransform: "none",
                    fontSize: "1rem",
                  }}
                >
                  {loading ? "Fetching..." : "Search"}
                </Button>
              </Grid>
            </Grid>

            {error && (
              <Typography color="error" align="center" mb={3}>
                {error}
              </Typography>
            )}

            {payslip && (
              <>
                <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                  Employee Information
                </Typography>

                <Grid container spacing={2} mb={3}>
                  <Grid item xs={12} md={6}>
                    <Typography>
                      <b>Name:</b> {staff?.name || "-"}
                    </Typography>
                    <Typography>
                      <b>PF No:</b> {staff?.pfno || "-"}
                    </Typography>
                    <Typography>
                      <b>Cadre:</b> {staff?.cadre || "-"}
                    </Typography>
                    <Typography>
                      <b>Designation:</b> {staff?.designation_name || "-"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography>
                      <b>Grade Level:</b> {staff?.grade_level || "-"}
                    </Typography>
                    <Typography>
                      <b>Salary Structure:</b> {staff?.salary_structure || "-"}
                    </Typography>
                    <Typography>
                      <b>Staff Group:</b> {staff?.staff_group || "-"}
                    </Typography>
                  </Grid>
                </Grid>

                <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                  Bank & Pension Information
                </Typography>

                <Grid container spacing={2} mb={3}>
                  <Grid item xs={12} md={6}>
                    <Typography>
                      <b>Bank Name:</b> {staff?.bank_name || "—"}
                    </Typography>
                    <Typography>
                      <b>Account No:</b> {staff?.bank_account_no || "—"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography>
                      <b>PFA Name:</b> {staff?.pfa_name || "—"}
                    </Typography>
                    <Typography>
                      <b>Pension PIN:</b> {staff?.pension_pin || "—"}
                    </Typography>
                  </Grid>
                </Grid>

                <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                  Earnings & Deductions
                </Typography>

                <TableContainer
                  sx={{
                    border: "1px solid #ddd",
                    borderRadius: 1,
                    overflow: "hidden",
                  }}
                >
                  <Table
                    size="small"
                    sx={{
                      tableLayout: "fixed",
                      width: "100%",
                      borderCollapse: "collapse",
                      "& th, & td": {
                        borderBottom: "1px solid #e6e6e6",
                        padding: "12px 16px",
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow sx={{ background: "#fafafa" }}>
                        <TableCell
                          sx={{ width: "25%", fontWeight: "700", borderRight: "1px solid #eee" }}
                        >
                          Component
                        </TableCell>
                        <TableCell
                          sx={{
                            width: "25%",
                            fontWeight: "700",
                            textAlign: "right",
                            borderRight: "2px solid #ccc",
                          }}
                        >
                          Amount (₦)
                        </TableCell>
                        <TableCell sx={{ width: "25%", fontWeight: "700", pl: 3 }}>
                          Component
                        </TableCell>
                        <TableCell sx={{ width: "25%", fontWeight: "700", textAlign: "right" }}>
                          Amount (₦)
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {(() => {
                        const base = salary?.base_salary_monthly ?? salary?.base_salary ?? null;

                        const earnings = [
                          ...(base !== null && base !== undefined
                            ? [{ name: "CONTISS Cons Salary", amount: base }]
                            : []),
                          ...earningsArr.map((e) => ({
                            name: e?.name ?? e?.component ?? e?.title ?? "Earning",
                            amount: e?.amount ?? e?.value ?? e?.monthly_amount ?? null,
                          })),
                        ];

                        const extras = [
                          { name: "SSANU Due", amount: salary?.UnionDue_monthly },
                          { name: "Employee Pension", amount: salary?.Pension_monthly },
                          { name: "National Housing Fund", amount: salary?.NHF_monthly },
                          { name: "Income Tax", amount: salary?.tax_monthly },
                        ].filter(
                          (x) => x.amount !== null && x.amount !== undefined && x.amount !== ""
                        );

                        const deductions = [
                          ...deductionsArr.map((d) => ({
                            name: d?.name ?? d?.component ?? d?.title ?? "Deduction",
                            amount: d?.amount ?? d?.value ?? d?.monthly_amount ?? null,
                          })),
                          ...extras,
                        ];

                        const maxRows = Math.max(earnings.length, deductions.length, 1);

                        return Array.from({ length: maxRows }).map((_, i) => (
                          <TableRow key={i} sx={{ "&:hover": { backgroundColor: "#fafafa" } }}>
                            {/* Earnings */}
                            <TableCell sx={{ borderRight: "1px solid #eee" }}>
                              {earnings[i]?.name || ""}
                            </TableCell>
                            <TableCell sx={{ textAlign: "right", borderRight: "2px solid #ccc" }}>
                              {earnings[i]?.amount !== null &&
                              earnings[i]?.amount !== undefined &&
                              earnings[i]?.amount !== ""
                                ? `₦${money(earnings[i]?.amount)}`
                                : ""}
                            </TableCell>

                            {/* Deductions */}
                            <TableCell sx={{ pl: 3 }}>{deductions[i]?.name || ""}</TableCell>
                            <TableCell sx={{ textAlign: "right" }}>
                              {deductions[i]?.amount !== null &&
                              deductions[i]?.amount !== undefined &&
                              deductions[i]?.amount !== ""
                                ? `₦${money(deductions[i]?.amount)}`
                                : ""}
                            </TableCell>
                          </TableRow>
                        ));
                      })()}

                      {/* Totals (safe) */}
                      <TableRow sx={{ backgroundColor: "#fffbe6" }}>
                        <TableCell sx={{ fontWeight: "700" }}>Total Gross Earnings</TableCell>
                        <TableCell sx={{ textAlign: "right", fontWeight: "700" }}>
                          {salary?.gross_income_monthly != null
                            ? `₦${money(salary.gross_income_monthly)}`
                            : "-"}
                        </TableCell>
                        <TableCell sx={{ fontWeight: "700", color: "red" }}>
                          Total Deductions
                        </TableCell>
                        <TableCell sx={{ textAlign: "right", fontWeight: "700", color: "red" }}>
                          {salary?.total_deductions != null
                            ? `₦${money(salary.total_deductions)}`
                            : "-"}
                        </TableCell>
                      </TableRow>

                      <TableRow sx={{ backgroundColor: "#f1f8e9" }}>
                        <TableCell sx={{ fontWeight: "700" }}>Net Pay</TableCell>
                        <TableCell sx={{ textAlign: "right", fontWeight: "700", color: "#2e7d32" }}>
                          {salary?.net_pay_monthly != null
                            ? `₦${money(salary.net_pay_monthly)}`
                            : "-"}
                        </TableCell>
                        <TableCell />
                        <TableCell />
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography align="center" sx={{ mt: 5, fontStyle: "italic", color: "gray" }}>
                  *** This is a system-generated payslip and does not require a signature ***
                </Typography>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
      <Footer />
    </DashboardLayout>
  );
}

export default PayslipDetails;
